import React, { useCallback, useEffect, useState } from 'react';
import { type ComponentProps } from 'react';

import { Flex, Modal, ModalBody, ModalCloseButton, ModalContent, ModalOverlay } from '@vimeo/bokeh';
import { useCampaignIdOverride } from '@vimeo/orion';
import { useOrionSettings } from '@vimeo/orion';
import {
  CardComparisonPaywall,
  GenericLimitPaywall,
  PrivacyLimitPaywall,
  StorageLimitPaywall,
  TableComparisonPaywall,
} from '@vimeo/paywalls';
import { type PaywallTrackingProps, usePaywallTracking } from '@vimeo/picox';

import TrackingContext from './components/common/TrackingContext';
import CustomModal from './components/custom/CustomModal';
import DefaultModal from './components/default/DefaultModal';
import EnterpriseModal from './components/enterprise/EnterpriseModal';
import { fetchUserData, fetchPlansData } from './utils/api';
import BpTracking from './utils/bpTracking';
import { PLANS } from './utils/constants';
import { modalProps, PlanData, UserData, TemplateType } from './utils/interface';
import {
  isBspLateStagePaywallKind,
  resolvePrivacyOptionFromTrigger,
  resolveTier,
  resolveVariant,
  type LateStagePaywallKind,
  type LateStagePrivacyOption,
  type LateStageVariant,
} from './utils/lateStagePaywallResolver';
import {
  isEligibleForFreeTrial,
  isSeatTierPlan,
  getDeviceType,
  hasDisplayPlan,
} from './utils/utils';

const EXCLUDED_TEMPLATE_TYPES: Array<TemplateType> = ['feature'];
type ComparisonPaywallTier = ComponentProps<typeof CardComparisonPaywall>['tier'];
type ComparisonPaywallCurrentTier = ComponentProps<typeof CardComparisonPaywall>['currentTier'];

const renderLateStagePaywall = ({
  kind,
  paywallTracking,
  variant,
  tier,
  currentTier,
  privacyOption,
  isUserEligibleForFreeTrial,
}: {
  kind: LateStagePaywallKind;
  paywallTracking: PaywallTrackingProps;
  variant: LateStageVariant;
  tier: PLANS;
  currentTier: PLANS | null;
  privacyOption: LateStagePrivacyOption | null;
  isUserEligibleForFreeTrial: boolean;
}) => {
  switch (variant) {
    case 'storage':
      return (
        <StorageLimitPaywall
          tier={tier}
          paywallTracking={paywallTracking}
          isUserEligibleForFreeTrial={isUserEligibleForFreeTrial}
        />
      );
    case 'privacy':
      return privacyOption ? (
        <PrivacyLimitPaywall
          tier={tier}
          privacyOption={privacyOption}
          paywallTracking={paywallTracking}
          isUserEligibleForFreeTrial={isUserEligibleForFreeTrial}
        />
      ) : (
        <GenericLimitPaywall
          tier={tier}
          paywallTracking={paywallTracking}
          isUserEligibleForFreeTrial={isUserEligibleForFreeTrial}
        />
      );
    default:
      if (kind === 'bsp_cards_generic_and_bsp_limits') {
        return (
          <CardComparisonPaywall
            tier={tier as ComparisonPaywallTier}
            currentTier={currentTier as ComparisonPaywallCurrentTier}
            paywallTracking={paywallTracking}
            isUserEligibleForFreeTrial={isUserEligibleForFreeTrial}
          />
        );
      }
      if (kind === 'bsp_table_generic_and_bsp_limits') {
        return (
          <TableComparisonPaywall
            tier={tier as ComparisonPaywallTier}
            currentTier={currentTier as ComparisonPaywallCurrentTier}
            paywallTracking={paywallTracking}
            isUserEligibleForFreeTrial={isUserEligibleForFreeTrial}
          />
        );
      }
      return (
        <GenericLimitPaywall
          tier={tier}
          paywallTracking={paywallTracking}
          isUserEligibleForFreeTrial={isUserEligibleForFreeTrial}
        />
      );
  }
};

interface State {
  isShowing: boolean;
  userData: null | UserData;
  plansData: null | PlanData[];
  hasTrackedImpression: boolean;
}

function UpsellModal(props: modalProps) {
  const { apiUrl, userConfig } = props;
  const { settings } = useOrionSettings();
  const [state, setState] = useState<State>({
    isShowing: false,
    userData: null,
    plansData: null,
    hasTrackedImpression: false,
  });
  const { trackPaywallDismissed } = usePaywallTracking({
    ...props.tracking.paywallTracking,
    isVisible: state.isShowing,
  });

  const campaignOverride = useCampaignIdOverride();

  // Destructure props.onClose to avoid re-rendering the component when it changes
  const propsOnClose = props.onClose;
  const handleClose = useCallback(() => {
    trackPaywallDismissed();
    propsOnClose();
  }, [trackPaywallDismissed, propsOnClose]);

  useEffect(() => {
    const getUserData = async () => {
      const userData = await fetchUserData(apiUrl, userConfig.jwt);

      return userData;
    };

    const getPlansData = async () => {
      return await fetchPlansData(apiUrl, userConfig, campaignOverride);
    };

    (async () => {
      const [userData, plansData] = await Promise.all([getUserData(), getPlansData()]);

      setState((prevState: State) => ({
        ...prevState,
        isShowing: true,
        userData: userData,
        plansData: plansData,
      }));
      BpTracking.configure(userData, userConfig.userId);

      if (props.tracking.onOpen) {
        props.tracking.onOpen();
      }
    })();
  }, [props.tracking, apiUrl, userConfig, campaignOverride]);

  useEffect(() => {
    const startTime = Date.now();

    if (!state.hasTrackedImpression && state.userData?.membership?.type) {
      BpTracking.trackImpression(
        {
          ...props.tracking.params,
          loading_time: (Date.now() - startTime) / 1000,
          is_new_pricing: isSeatTierPlan(state.userData?.membership?.type),
          device_type: getDeviceType(),
        },
        props.tracking.bpEventVersion,
      );
      setState((prevState: State) => ({
        ...prevState,
        hasTrackedImpression: true,
      }));
    }
  }, [props.tracking, state.userData?.membership?.type, state.hasTrackedImpression]);

  const userTier = state.userData?.membership?.type;
  let templateType = props.templateType;
  if (
    !EXCLUDED_TEMPLATE_TYPES.includes(props.templateType) &&
    (userTier === PLANS.ADVANCED ||
      userTier === PLANS.PREMIUM ||
      userTier === PLANS.TEAM_LIVE ||
      userTier === PLANS.CUSTOM_SELF_SERVE) &&
    !(hasDisplayPlan(props.modalConfig) && props.modalConfig?.displayPlan)
  ) {
    templateType = 'enterprise';
  }

  const paywallFeature = props.tracking.paywallTracking.paywallFeature;
  const paywallTrigger = props.tracking.paywallTracking.paywallTrigger;
  const shouldUseBspLateStage = isBspLateStagePaywallKind(settings.late_stage_paywall_kind);
  const privacyOption = resolvePrivacyOptionFromTrigger(paywallTrigger);
  const baseVariant = resolveVariant(paywallFeature);
  const variant: LateStageVariant =
    baseVariant === 'privacy' && !privacyOption ? 'generic' : baseVariant;
  const shouldUseDefaultInsteadOfBspGeneric =
    settings.late_stage_paywall_kind === 'limits_only_bsp' && variant === 'generic';
  const normalizedPaywallTrigger = paywallTrigger?.toLowerCase() ?? '';
  // Both cold-storage and cold-privacy banners are churn-driven, so they share
  // the same tier setting for the late-stage paywall.
  const isColdStorageTrigger =
    normalizedPaywallTrigger.includes('cold_storage') ||
    normalizedPaywallTrigger.includes('cold_privacy');
  const tierSetting = isColdStorageTrigger
    ? settings.cold_storage_trigger_paywall_tier
    : settings.limit_based_paywall_tier;
  const lateStageTier =
    state.userData?.membership?.type && state.plansData
      ? resolveTier({
          currentTier: state.userData?.membership?.type,
          tierSetting,
          plansData: state.plansData,
          excludePlans: (props.modalConfig as any)?.['excludePlans'],
          variant,
        })
      : null;
  const isComparisonLateStagePaywallKind =
    settings.late_stage_paywall_kind === 'bsp_cards_generic_and_bsp_limits' ||
    settings.late_stage_paywall_kind === 'bsp_table_generic_and_bsp_limits';
  const shouldFallbackToLegacyFromInvalidComparisonTier =
    variant === 'generic' &&
    isComparisonLateStagePaywallKind &&
    !([PLANS.STARTER, PLANS.STANDARD, PLANS.ADVANCED] as (PLANS | null)[]).includes(lateStageTier);
  const isComparisonLateStagePaywall = variant === 'generic' && isComparisonLateStagePaywallKind;
  const lateStagePaywallDesktopWidth = isComparisonLateStagePaywall ? '760px' : '560px';
  const shouldRenderBspLateStagePaywall =
    shouldUseBspLateStage &&
    props.templateType === 'default' &&
    templateType !== 'enterprise' &&
    Boolean(lateStageTier) &&
    !shouldUseDefaultInsteadOfBspGeneric &&
    !shouldFallbackToLegacyFromInvalidComparisonTier;
  const lateStagePaywallTracking = props.tracking.paywallTracking;
  const isUserEligibleForFreeTrial = state.userData
    ? isEligibleForFreeTrial(state.userData)
    : false;

  let modal;
  switch (templateType) {
    case 'default':
      modal =
        shouldRenderBspLateStagePaywall && lateStageTier ? (
          <Modal isOpen={true} onClose={handleClose} size={['full', 'md']} scrollBehavior="inside">
            <ModalOverlay />
            <ModalContent
              containerProps={{ padding: { base: '0 !important' } }}
              maxWidth={{ base: '100vw', md: lateStagePaywallDesktopWidth }}
              width={{ base: '100vw', md: lateStagePaywallDesktopWidth }}
              height={{ base: '100dvh', md: 'auto' }}
              minH={{ base: '100dvh', md: 'auto' }}
              maxH={{ base: '100dvh', md: '90vh' }}
              borderRadius={{ base: '0', md: '16px' }}
              margin={{ base: '0', md: 'auto' }}
              overflow="hidden"
              position="relative"
            >
              <ModalCloseButton onClick={handleClose} zIndex={1} />
              <ModalBody p="24px" display="flex" flex="1">
                <Flex width="100%">
                  {renderLateStagePaywall({
                    kind: settings.late_stage_paywall_kind,
                    paywallTracking: lateStagePaywallTracking,
                    variant,
                    tier: lateStageTier,
                    currentTier: state.userData?.membership?.type ?? null,
                    privacyOption,
                    isUserEligibleForFreeTrial,
                  })}
                </Flex>
              </ModalBody>
            </ModalContent>
          </Modal>
        ) : userTier === 'enterprise' ? (
          <EnterpriseModal
            userData={state.userData}
            onClose={handleClose}
            tracking={props.tracking}
            modalConfig={props.modalConfig}
          />
        ) : (
          <DefaultModal
            userData={state.userData}
            plansData={state.plansData}
            modalConfig={{
              hidePlansSummary: true,
              ...props.modalConfig,
            }}
            onClose={handleClose}
            tracking={props.tracking}
          />
        );
      break;
    case 'feature':
      modal = <CustomModal modalConfig={props.modalConfig} onClose={handleClose} />;
      break;
    case 'enterprise':
      modal = (
        <EnterpriseModal
          userData={state.userData}
          onClose={handleClose}
          tracking={props.tracking}
          modalConfig={props.modalConfig}
        />
      );
      break;
    default:
      modal = null;
  }

  if (!state.isShowing) return null;

  return (
    <TrackingContext.Provider
      value={{ ...props.tracking, device_type: getDeviceType(), campaignOverride }}
    >
      {modal}
    </TrackingContext.Provider>
  );
}

export default UpsellModal;
